<template>
  <div
    v-if="customCollabClientPayload"
    :class="{
      'work-status-snippet': true,
      'work-status-snippet--yellow-bg': !props.customCollabClientPayload.customerCollabInputs.markedCompleteDate,
      'work-status-snippet--green-bg': props.customCollabClientPayload.customerCollabInputs.markedCompleteDate,
    }"
  >
    <template v-if="props.customCollabClientPayload.creatorCollabInputs.agreedToDate">
      <!-- Header -->
      <div class="header work-status-snippet__header">
        <div class="global-h3 header__title">Work Status</div>
        <div class="header__subtitle">Keep the destination updated on how the work is progressing.</div>
      </div>
      <!-- / Header -->

      <div class="work-status-snippet__divider"></div>

      <!-- Work status controls -->
      <div class="work-status-controls work-status-snippet__work-status-controls">
        <div class="global-h4 work-status-controls__title">Status:</div>
        <!-- Work status dropdown -->
        <div
          :class="{
            'work-status-dropdown': true,
            'work-status-dropdown--disabled': props.isSaving,
            'work-status-controls__dropdown': true,
          }"
        >
          <CollabStatusStripe
            :class="!props.customCollabClientPayload.creatorCollabInputs.deliverablesReadyDate ? 'work-status-dropdown__status-stripe' : ''"
            :text="statusText"
            size="medium"
            :color="props.customCollabClientPayload.customerCollabInputs.markedCompleteDate ? 'green' : 'white'"
          />

          <span v-if="!props.customCollabClientPayload.creatorCollabInputs.deliverablesReadyDate" class="global-select-wrap work-status-dropdown__select">
            <span class="global-select-wrap__arrow"></span>
            <select class="global-select" v-model="props.customCollabClientPayload.creatorCollabInputs.status">
              <option v-for="(text, key) in props.customCollabClientPayload.creatorStatusOptions" :key="key" :value="key">
                {{ text }}
              </option>
            </select>
          </span>
        </div>
        <!-- / Work status dropdown -->
      </div>
      <!-- / Work status controls -->

      <div class="work-status-snippet__divider"></div>

      <!-- Notes form -->
      <div class="notes-form work-status-snippet__notes-form">
        <div class="global-h4 notes-form__title">
          Summary Notes
          <InfoIconWithTooltip :width="400" style="margin-left: 10px"
            >Use notes for an overview of the current status of the work.<br />
            <div style="margin-top: 5px">Messaging is best for questions or to coordinate any details.</div>
          </InfoIconWithTooltip>
        </div>
        <div style="margin-bottom: 5px">The destination will see these notes.</div>
        <textarea
          class="global-textarea notes-form__textarea"
          v-model="props.customCollabClientPayload.creatorCollabInputs.creatorNotes"
          :disabled="props.isSaving"
          placeholder="Share an update on your progress and next steps"
        ></textarea>
      </div>
      <!-- / Notes form -->

      <DeliverablesForm
        class="work-status-snippet__deliverables-form"
        :customCollabStatus="props.customCollabClientPayload.creatorCollabInputs.status"
        :deliverablesAreReadyDate="props.customCollabClientPayload.creatorCollabInputs.deliverablesReadyDate"
        v-model:deliverables="props.customCollabClientPayload.creatorCollabInputs.deliverables"
        @click:deliverablesAreReady="emit('click:deliverablesAreReady')"
        :isSaving="props.isSaving"
      />

      <div class="work-status-snippet__bottom-panel">
        <NoteWithIcon size="tiny" :isTransparentBg="true" color="gray">
          <template #icon><IconEmbedded name="info-simple_4" :size="18" /></template>
          <template #text><div>The destination will be notified when updates are made</div></template>
        </NoteWithIcon>

        <SrpButton @click="emit('save')" :isDisabled="props.isSaving">
          <template #icon>
            <LoadingIcon v-if="props.isSaving" :size="22" />
            <IconEmbedded v-else name="save_2" :size="22" />
          </template>
          Save
        </SrpButton>
      </div>
    </template>
    <div v-else-if="props.customCollabClientPayload.customerCollabInputs.formalizedDate">
      <!-- Header -->
      <div class="header work-status-snippet__header">
        <div class="global-h3 header__title">Accept the Work Agreement?</div>
        <div class="header__subtitle">
          <div>The destination has requested the formal work agreement below.</div>
          <div style="margin-top: 10px">If you have questions or need changes, please message the destination.</div>
          <div style="margin-top: 12px">
            <SrpButton @click="emit('showSendForCreatorAcceptance')">Accept Work Agreement</SrpButton>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <!-- Header -->
      <div class="header work-status-snippet__header">
        <h2 class="global-h2 header__title">What's Next?</h2>
        <div class="header__subtitle">
          <div>The destination would like to work with you!</div>
          <div style="margin-top: 10px"><b>Review the messages and help the destination formalize the work agreement.</b></div>
          <div style="margin-top: 12px">
            <SrpButton size="small" fill="outlined" :isDisabled="true">Accept Work Agreement</SrpButton>
            <InfoIconWithTooltip :width="300" style="margin-left: 10px">Available after the destination sends the work agreement.</InfoIconWithTooltip>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

// Types
import { CustomCollabClientPayload } from "@contracts/customCollabs";

// Components
import CollabStatusStripe from "@components/CollabStatusStripe.vue";
import DeliverablesForm from "@views/CustomCollabs/CreatorCustomCollabDetails/DeliverablesForm.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";

const props = withDefaults(
  defineProps<{
    customCollabClientPayload: CustomCollabClientPayload | null;
    isSaving: boolean;
  }>(),
  {
    customCollabClientPayload: null,
    isSaving: false,
  }
);

const emit = defineEmits<{
  (event: "save"): void;
  (event: "showSendForCreatorAcceptance"): void;
  (event: "click:deliverablesAreReady"): void;
}>();

const statusText = computed(() => {
  const status = props.customCollabClientPayload?.creatorCollabInputs.status;
  const options = props.customCollabClientPayload?.creatorStatusOptions;
  if (status && options && status in options) {
    return options[status];
  }
  return props.customCollabClientPayload?.statusCreatorText;
});
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";

// Header =====================================================================
.header {
  &__title {
    margin-bottom: 3px;
  }

  &__subtitle {
  }
}

// Work status dropdown =======================================================
.work-status-dropdown {
  position: relative;
  z-index: 0;

  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &__status-stripe {
    position: absolute;
    inset: 6px auto auto 10px;
    z-index: 1;
    pointer-events: none;
  }

  &__select {
    select {
      padding-right: 70px;
    }
  }
}

// Work status controls =======================================================
.work-status-controls {
  display: inline-flex;
  align-items: center;

  &__title {
    margin-right: 7px;
  }

  &__dropdown {
  }
}

// Notes form =================================================================
.notes-form {
  &__title {
    margin-bottom: 6px;
  }

  &__textarea {
    height: 100px;
    min-height: 70px;
  }
}

// Work status snippet ========================================================
.work-status-snippet {
  @include standardFont;
  padding: 20px 25px;
  border-radius: 6px;
  background: rgba(240, 240, 240, 1);

  &--yellow-bg {
    background: rgba(240, 231, 184, 1);
  }
  &--green-bg {
    background: rgba(213, 236, 206, 1);
  }

  &__header {
    margin-bottom: 14px;
  }

  &__divider {
    margin-bottom: 14px;
    border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
  }

  &__work-status-controls {
    margin-bottom: 18px;
  }

  &__notes-form {
    margin-bottom: 23px;
  }

  &__deliverables-form {
    margin-bottom: 20px;
  }

  &__bottom-panel {
    gap: 12px 22px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
  }
}
</style>
